import React, { useState, useEffect } from 'react';
import { Save, Lock, Bell, User, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuth from '../hooks/useAuth';
import adminService from '../services/adminService';

export default function Settings() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [password, setPassword] = useState({ current: '', new: '', confirm: '' });
  const [notifications, setNotifications] = useState({
    userSignups: true,
    newBookings: true,
    userSignupRecipients: [],
    bookingReminderHoursBefore: 5,
  });
  const [recipientDraft, setRecipientDraft] = useState('');
  const [notificationsReady, setNotificationsReady] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [saving, setSaving] = useState({ profile: false, password: false, notifications: false });

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', email: user.email || '' });
    }
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    setNotificationsReady(false); setNotificationsError('');
    try {
      const data = await adminService.getSettings();
      if (data.notifications) {
        setNotifications((current) => ({
          ...current,
          ...data.notifications,
          userSignupRecipients: data.notifications.userSignupRecipients || [],
          bookingReminderHoursBefore: data.notifications.bookingReminderHoursBefore ?? 5,
        }));
        setNotificationsReady(true);
      } else {
        setNotificationsError('Notification preferences could not be loaded. Retry before saving.');
      }
    } catch {
      setNotificationsError('Notification preferences could not be loaded. Retry before saving.');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving((s) => ({ ...s, profile: true }));
    try {
      await adminService.updateSettings({ name: profile.name, email: profile.email });
      toast.success('Profile updated');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving((s) => ({ ...s, profile: false }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (password.new !== password.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (password.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving((s) => ({ ...s, password: true }));
    try {
      await adminService.changePassword(password.current, password.new);
      toast.success('Password changed successfully');
      setPassword({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving((s) => ({ ...s, password: false }));
    }
  };

  const handleNotificationsUpdate = async () => {
    if (!notificationsReady || saving.notifications) return;
    setSaving((s) => ({ ...s, notifications: true }));
    try {
      const data = await adminService.updateSettings({ notifications });
      if (data?.notifications) setNotifications(data.notifications);
      toast.success('Notification preferences saved');
    } catch (err) {
      const data = err.response?.data;
      const message = data?.message || data?.title || 'Failed to save notification preferences';
      toast.error(`${message}${data?.traceId ? ` (Trace ID: ${data.traceId})` : ''}`);
    } finally {
      setSaving((s) => ({ ...s, notifications: false }));
    }
  };

  const addRecipient = () => {
    const email = recipientDraft.trim().toLowerCase();
    if (!email) return;

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValidEmail) {
      toast.error('Enter a valid email address');
      return;
    }
    if (notifications.userSignupRecipients.some((recipient) => recipient.toLowerCase() === email)) {
      toast.error('This email address is already included');
      return;
    }
    if (notifications.userSignupRecipients.length >= 20) {
      toast.error('You can add up to 20 recipients');
      return;
    }

    setNotifications((current) => ({
      ...current,
      userSignupRecipients: [...current.userSignupRecipients, email],
    }));
    setRecipientDraft('');
  };

  const removeRecipient = (email) => {
    setNotifications((current) => ({
      ...current,
      userSignupRecipients: current.userSignupRecipients.filter((recipient) => recipient !== email),
    }));
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-[#1F2937]">Settings</h1>
        <p className="text-sm text-[#667085] mt-1">Manage your account and preferences</p>
      </div>

      <div className="admin-card">
        <h3 className="text-sm font-semibold text-[#1F2937] mb-4 flex items-center gap-2">
          <User size={18} className="text-admin-600" />
          Profile Information
        </h3>
        <form onSubmit={handleProfileUpdate} className="space-y-4 max-w-md">
          <div>
            <label className="admin-label">Full Name</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
              className="admin-input"
            />
          </div>
          <div>
            <label className="admin-label">Email Address</label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
              className="admin-input"
            />
          </div>
          <button type="submit" disabled={saving.profile} className="admin-btn-primary">
            {saving.profile ? 'Saving...' : <span className="flex items-center gap-2"><Save size={16} /> Save Changes</span>}
          </button>
        </form>
      </div>

      <div className="admin-card">
        <h3 className="text-sm font-semibold text-[#1F2937] mb-4 flex items-center gap-2">
          <Lock size={18} className="text-admin-600" />
          Change Password
        </h3>
        <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
          <div>
            <label className="admin-label">Current Password</label>
            <input
              type="password"
              value={password.current}
              onChange={(e) => setPassword((p) => ({ ...p, current: e.target.value }))}
              className="admin-input"
            />
          </div>
          <div>
            <label className="admin-label">New Password</label>
            <input
              type="password"
              value={password.new}
              onChange={(e) => setPassword((p) => ({ ...p, new: e.target.value }))}
              className="admin-input"
            />
          </div>
          <div>
            <label className="admin-label">Confirm New Password</label>
            <input
              type="password"
              value={password.confirm}
              onChange={(e) => setPassword((p) => ({ ...p, confirm: e.target.value }))}
              className="admin-input"
            />
          </div>
          <button type="submit" disabled={saving.password} className="admin-btn-primary">
            {saving.password ? 'Updating...' : <span className="flex items-center gap-2"><Lock size={16} /> Update Password</span>}
          </button>
        </form>
      </div>

      <div className="admin-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2">
            <Bell size={18} className="text-admin-600" />
            Notification Preferences
          </h3>
          <button
            onClick={handleNotificationsUpdate}
            disabled={saving.notifications || !notificationsReady}
            className="admin-btn-primary text-sm"
          >
            {saving.notifications ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
        {notificationsError && <div role="alert" className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{notificationsError}<button type="button" onClick={loadSettings} className="ml-2 underline">Retry loading preferences</button></div>}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#1F2937]">New verified users</p>
              <p className="text-xs text-[#667085]">Email the recipients below when a player or venue owner verifies their account</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.userSignups}
                onChange={(e) => setNotifications((previous) => ({ ...previous, userSignups: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-[#F3F4F6] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-admin-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#D0D5DD] after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-admin-600" />
            </label>
          </div>

          <div className="flex items-center justify-between border-t border-[#EAECF0] pt-4">
            <div>
              <p className="text-sm font-medium text-[#1F2937]">New confirmed bookings</p>
              <p className="text-xs text-[#667085]">Email the recipients below when a player confirms and pays for a court booking</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.newBookings}
                onChange={(e) => setNotifications((previous) => ({ ...previous, newBookings: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-[#F3F4F6] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-admin-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#D0D5DD] after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-admin-600" />
            </label>
          </div>

          <div className="border-t border-[#EAECF0] pt-4">
            <div className="mb-3">
              <p className="text-sm font-medium text-[#1F2937]">Admin notification recipients</p>
              <p className="text-xs text-[#667085]">Add up to 20 operational or admin inboxes for the enabled notifications above.</p>
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={recipientDraft}
                onChange={(e) => setRecipientDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder="admin@example.com"
                className="admin-input flex-1"
                disabled={!notifications.userSignups && !notifications.newBookings}
              />
              <button
                type="button"
                onClick={addRecipient}
                disabled={!notifications.userSignups && !notifications.newBookings}
                className="admin-btn-secondary shrink-0 flex items-center gap-1"
              >
                <Plus size={16} /> Add
              </button>
            </div>
            {notifications.userSignupRecipients.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {notifications.userSignupRecipients.map((email) => (
                  <span key={email} className="inline-flex items-center gap-1 rounded-full bg-admin-50 px-3 py-1 text-xs font-medium text-admin-700">
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      aria-label={`Remove ${email}`}
                      className="rounded-full p-0.5 hover:bg-admin-100"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-xs text-[#98A2B3]">No recipients configured yet.</p>
            )}
          </div>

          <div className="border-t border-[#EAECF0] pt-4">
            <label htmlFor="bookingReminderHours" className="text-sm font-medium text-[#1F2937]">Booking reminder lead time</label>
            <p className="mt-1 text-xs text-[#667085]">Players receive an in-app, push, or email reminder this many hours before a confirmed booking. Default: 5 hours.</p>
            <div className="mt-3 flex max-w-xs items-center gap-2">
              <input
                id="bookingReminderHours"
                type="number"
                min="1"
                max="168"
                step="1"
                value={notifications.bookingReminderHoursBefore}
                onChange={(e) => setNotifications((current) => ({
                  ...current,
                  bookingReminderHoursBefore: Number(e.target.value),
                }))}
                className="admin-input w-24"
              />
              <span className="text-sm text-[#667085]">hours before play</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
